import { Device } from 'mediasoup-client';
import type { Consumer, RtpCapabilities, Transport } from 'mediasoup-client/types';

import type {
  ConsumerParams,
  MediasoupClientEventSender,
  MediasoupConsumeResult,
  MediasoupConsumerStatsSender,
  MediasoupPlatform,
  MediasoupRecvTransportCallbacks,
  TransportParams,
} from '@ngsos/portal-core/video';

let device: Device | undefined = undefined;
let consumerTransport: Transport | undefined = undefined;
let videoConsumer: Consumer | undefined = undefined;
let statsIntervalId: number | undefined = undefined;
let statsTimeoutId: number | undefined = undefined;

const STATS_COLLECTION_INTERVAL_MS = 5000;
const STATS_COLLECTION_DURATION_MS = 60000;

let clientEventSender: MediasoupClientEventSender | undefined;
let clientEventSenderReady: Promise<void>;
let resolveClientEventSender: () => void;

function resetClientEventSenderPromise() {
  clientEventSenderReady = new Promise((resolve) => {
    resolveClientEventSender = resolve;
  });
}

resetClientEventSenderPromise();

function registerClientEventSender(send: MediasoupClientEventSender) {
  clientEventSender = send;
  resolveClientEventSender();
}

function unregisterClientEventSender() {
  clientEventSender = undefined;
  resetClientEventSenderPromise();
}

async function sendClientEvent(eventName: string, data?: string) {
  await clientEventSenderReady;
  await clientEventSender?.(eventName, data);
}

async function createDevice(routerRtpCapabilities: RtpCapabilities) {
  await sendClientEvent('createDevice() Started');
  void sendBrowserAndUserInfoWithoutRaisingTheErrorBar();
  device = new Device();
  await device.load({ routerRtpCapabilities });

  await sendClientEvent('createDevice() Succeeded');
  return device.rtpCapabilities;
}

async function sendBrowserAndUserInfoWithoutRaisingTheErrorBar() {
  try {
    const w = window as unknown as {
      getBrowserDeviceInfo?: () => Promise<string> | string;
      getUserInfo?: () => string;
    };
    const deviceInfo = typeof w.getBrowserDeviceInfo === 'function' ? await w.getBrowserDeviceInfo() : '';
    const userInfo = typeof w.getUserInfo === 'function' ? w.getUserInfo() : '';
    const info = (deviceInfo + userInfo).trim();
    if (info) {
      await sendClientEvent('BrowserInfo', info);
    }
  } catch {
    return;
  }
}

async function createRecvTransport(transportParams: TransportParams, callbacks: MediasoupRecvTransportCallbacks) {
  const params = transportParams;

  await sendClientEvent('createRecvTransport() Started');

  if (device === undefined) {
    throw new Error('createRecvTransport error: Device is not defined');
  }

  consumerTransport = device.createRecvTransport(params);

  consumerTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
    try {
      await callbacks.onConnect(dtlsParameters);

      callback();
    } catch (e) {
      errback(e as Error);
    }
  });

  consumerTransport.on('connectionstatechange', async (rtcPeerConnectionState) => {
    console.log('Consumer transport connectionstatechange:', rtcPeerConnectionState);
    switch (rtcPeerConnectionState) {
      case 'connecting':
        break;
      case 'connected':
        await sendClientEvent('Transport Connected');
        break;
      case 'failed':
        await sendClientEvent('Transport Connection Failed');
        await callbacks.onConnectionFailed();
        break;
      case 'disconnected':
        await sendClientEvent('Transport Disconnected');
        break;
      case 'closed':
        await sendClientEvent('Transport Closed');
        break;
      default:
        break;
    }
  });
}

async function consume(consumerParams: ConsumerParams): Promise<MediasoupConsumeResult> {
  console.log('JS: consume called with params:', consumerParams);
  await sendClientEvent('consume() Started');
  if (consumerTransport === undefined) {
    throw new Error('Consume error: Consumer transport is not defined');
  }

  if (videoConsumer && !videoConsumer.closed) {
    videoConsumer.close();
    videoConsumer = undefined;
  }

  videoConsumer = await consumerTransport.consume(consumerParams);
  await sendClientEvent('consume() Succeeded');
  console.log('JS: consume finished');

  return { track: videoConsumer.track };
}

async function play(consumerParams: ConsumerParams) {
  await sendClientEvent('play() Started');
  console.log('JS: play() started');

  const remoteVideo = document.getElementById('remoteVideo') as HTMLVideoElement | null;

  if (!remoteVideo) {
    await sendClientEvent('play() Failed - remoteVideo element not found');
    throw new Error('play() Failed: remoteVideo element is not defined');
  }

  if (videoConsumer === undefined) {
    await sendClientEvent('play() Failed - videoConsumer not found');
    throw new Error('play() Failed: videoConsumer is not defined');
  }

  const { track } = videoConsumer;

  const stream = new MediaStream([track]);

  remoteVideo.srcObject = stream;

  const videoViewer = remoteVideo.closest('.mediasoup-video-viewer');

  if (videoViewer && consumerParams.videoRotation && consumerParams.videoRotation !== 'None') {
    console.log('JS: Applying rotation class to video viewer for Firefox');
    videoViewer.classList.add('rotate');
  }

  try {
    await sendClientEvent('calling remoteVideo.play()');
    console.log('calling remoteVideo.play()');
    await remoteVideo.play();
    await sendClientEvent('remoteVideo.play() Succeeded');
    console.log('JS: remoteVideo.play() SUCCEEDED ');
  } catch (err) {
    await sendClientEvent('remoteVideo.play() Failed - Error: ' + String(err));
    console.error('Consume error: remoteVideo.play() failed', err);
  }
}

async function closeConsumer() {
  stopStatsCollection();
  await sendClientEvent('consumerEndCall() called');

  const remoteVideo = document.getElementById('remoteVideo');
  const videoViewer = remoteVideo?.closest('.mediasoup-video-viewer');
  if (videoViewer) {
    videoViewer.classList.remove('rotate');
  }

  if (videoConsumer) {
    videoConsumer.close();
    videoConsumer = undefined;
  }

  if (consumerTransport) {
    consumerTransport.close();
    consumerTransport = undefined;
  }
}

function dispose() {
  stopStatsCollection();

  if (videoConsumer) {
    if (!videoConsumer.closed) {
      videoConsumer.close();
    }
    videoConsumer = undefined;
  }

  if (consumerTransport) {
    if (!consumerTransport.closed) {
      consumerTransport.close();
    }
    consumerTransport = undefined;
  }

  device = undefined;
}

function collectConsumerStats(send: MediasoupConsumerStatsSender) {
  if (statsIntervalId) return;

  console.log('Consumer stats collection started (1 minute)');
  void sendClientEvent('ConsumerStatsCollection() Started');

  statsIntervalId = window.setInterval(async () => {
    if (consumerTransport) {
      try {
        const pc = (consumerTransport as unknown as { _handler: { _pc: RTCPeerConnection } })._handler._pc;
        const stats = await pc.getStats();
        const statsObj: Record<string, unknown> = {};
        stats.forEach((report: { id: string }) => {
          statsObj[report.id] = report;
        });
        await send(statsObj);
      } catch (err) {
        console.error('Failed to collect consumer stats:', err);
      }
    }
  }, STATS_COLLECTION_INTERVAL_MS);

  statsTimeoutId = window.setTimeout(() => {
    console.log('Consumer stats collection timeout reached');
    stopStatsCollection();
  }, STATS_COLLECTION_DURATION_MS);
}

function stopStatsCollection() {
  if (statsIntervalId) {
    clearInterval(statsIntervalId);
    statsIntervalId = undefined;
    console.log('Consumer stats collection stopped');
    void sendClientEvent('ConsumerStatsCollection() Stopped');
  }

  if (statsTimeoutId) {
    clearTimeout(statsTimeoutId);
    statsTimeoutId = undefined;
  }
}

export const mediasoupPlatform: MediasoupPlatform = {
  registerClientEventSender,
  unregisterClientEventSender,
  createDevice,
  createRecvTransport,
  consume,
  play,
  closeConsumer,
  dispose,
  collectConsumerStats,
};
